<template>
  <div class="nx-file-image-selector-wrapper">
    <label v-if="label" class="primary--text" :class="{ 'error--text': hasErrors }">
      {{ label }}
    </label>
    <div class="nx-file-image-selector">
      <v-card :class="`v-card--${template}`">
        <template v-if="hasImage">
          <nx-image
            v-if="selectedFileIsImage"
            class="image grey lighten-3"
            :src="selectedFile.publicUrl"
            :height="calculateHeight"
            maxHeight="190"
            :alt="selectedFile.name"
            v-bind="$attrs"
          ></nx-image>
          <video v-if="selectedFileIsVideo" width="100%" height="260" autoplay loop muted>
            <source :src="selectedFile.publicUrl" type="video/mp4" />
          </video>
          <v-tooltip v-if="hint" top class="nx-file-image-selector__hint ma-1">
            <v-icon slot="activator">info</v-icon>
            <span>{{ hint }}</span>
          </v-tooltip>

        </template>

        <div
          v-else
          class="image-placeholder grey lighten-3 grey--text"
        >
          <p class="text" v-show="!loading">{{ $t('message.file.imageSelector.noimage') }}</p>
          <p class="text" v-show="loading">
            {{ $t('message.file.imageSelector.loading') }}
            <br>
            <v-progress-circular
              indeterminate
              color="secondary"
            ></v-progress-circular>
          </p>
        </div>

        <v-card-actions>
          <v-layout row align-center justify-space-around>
            <nx-selector
              :label="label"
              :additionalFilters="processedFilters"
              :value="value"
              :title-length="titleLength"
              @input="fileSelected"
              v-bind="$attrs"
              :allow-searcher="allowSearcher"
              :collection-id="collectionId"
              :error-messages="errorMessages"
            ></nx-selector>

            <v-tooltip top>
              <v-btn
                class="mt-2"
                icon
                slot="activator"
                @click="openFileUploadDialog"
                v-bind="$attrs"
              >
                <v-icon>file_upload</v-icon>
              </v-btn>
              <span>{{ $t('message.file.imageSelector.upload')}}</span>
            </v-tooltip>

          </v-layout>
        </v-card-actions>
      </v-card>
    </div>

    <nx-file-wizard
      :manual-hint-text="manualHintText"
      :mimeType="mimeType"
      :active="openFileUpload"
      :type="fileType"
      :size="size"
      :before-upload-clippings-config="beforeUploadClippingsConfig"
      :before-upload-force-clip="beforeUploadForceClip"
      :before-upload-automatic-clip="beforeUploadAutomaticClip"
      :collectionId="collectionId"
      @dismiss="openFileUpload = false"
      @fileCreated="fileCreated"
    ></nx-file-wizard>

  </div>
</template>

<script>
import Loadable from '@/mixins/loadable';
import FileInput from '@/modules/file/mixins/file-upload';
import Image from '@/components/media/Image';
import NxFileWizard from '@/modules/file/wizard/Wizard';
import FileSelector from '@/modules/file/selectors/Selector';

export default {
  name: 'nx-file-image-selector',
  mixins: [Loadable, FileInput],
  components: {
    NxFileWizard,
    [Image.name]: Image,
    [FileSelector.name]: FileSelector,
  },
  props: {
    hint: String,
    errorMessages: {
      type: Array,
      default: () => [],
    },
    beforeUploadForceClip: {
      type: Boolean,
      default: false,
    },
    beforeUploadAutomaticClip: {
      type: Boolean,
      default: false,
    },
    beforeUploadClippingsConfig: {
      type: Array,
      default: () => ([]),
    },
    allowSearcher: {
      type: Boolean,
      default: false,
    },
    collectionId: {
      type: Number,
    },
    template: {
      type: String,
      default: 'lg',
    },
    manualHintText: {
      type: String,
    },
  },
  computed: {
    hasImage() {
      return this.selectedFile ? this.selectedFile.publicUrl : false;
    },
    hasErrors() {
      return this.errorMessages.length > 0;
    },
    selectedFileIsImage() {
      return this.selectedFile && this.selectedFile.mimeType.startsWith('image');
    },
    // Animated images webp/gif/mp4
    selectedFileIsVideo() {
      return this.selectedFile && this.selectedFile.mimeType.startsWith('video');
    },
    processedFilters() {
      return {
        mimeType: 'image/*',
        ...this.filters,
      };
    },
    mimeType() {
      return this.processedFilters.mimeType;
    },
    calculateHeight() {
      return this.template === 'sm' ? 100 : 160;
    },
  },
};
</script>

<style lang="stylus">
.nx-file-image-selector-wrapper {
  margin-bottom: 20px;

  label {
    font-size: 12px;
  }
}

.nx-file-image-selector {
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .image-placeholder {
    animation: fadeIn 1s;
    display: flex;
    padding-top: 22%;
    align-items: center;
    align-content: center;
    justify-content: center;
    height: 150px;

    .text {
      text-align: center;
      position: relative;
      margin-top: -22%;
      animation: fadeIn 1s;
    }
  }

  .nx-file-selector {
    width: 93%;

    .v-input {
      margin-top: 5px;
    }

    .v-input__append-inner {
      margin-top: 10px;
    }
  }

  .v-card--sm {
    display: flex;

    .nx-image {
      height: 100px;
      padding: 0 15px;
      flex: 0 0 178px;
    }

    .image-placeholder {
      height: 100px;
      padding: 0;
      flex: 0 0 178px;

      .text {
        margin: 0 10px;
      }
    }

    .v-card__actions {
      margin: 0 0 0 20px;
      width: 100%;
      padding: 0;
    }
  }
}
.nx-file-image-selector__hint {
  position: absolute;
  top 0
  right: 0
}
</style>
